// StockManagementComponents.jsx - Fully Responsive Version

import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getApplicablePricingRulesThunk,
  getProductPricingRulesThunk,
  assignPricingRuleThunk,
  removePricingRuleThunk,
} from "../../../store/thunks/adminProductThunks";
import { getPricingRulesThunk } from "../../../store/thunks/pricingThunks";
import Dropdown from "../../../components/common/Dropdown";
import {
  FiInfo,
  FiTrash2,
  FiPlus,
  FiPackage,
  FiMapPin,
  FiAlertTriangle,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import toast from "react-hot-toast";
import Button from "components/common/Button";

export const PricingConfigSection = ({
  formData,
  onChange,
  permissions,
  productId,
  locationStocks = [],
  mode = "add",
}) => {
  const dispatch = useDispatch();
  const [localRuleAssignments, setLocalRuleAssignments] = useState({});
  const [loadingRules, setLoadingRules] = useState(false);
  const [expandedBranches, setExpandedBranches] = useState({});

  const { pricingRules } = useSelector((state) => state.pricing);

  // Initialize from formData
  useEffect(() => {
    if (formData.pricingConfig?.branchSpecificRules?.length > 0) {
      const assignments = {};
      formData.pricingConfig.branchSpecificRules.forEach((rule) => {
        if (rule.branchCode && rule.pricingRuleId) {
          assignments[rule.branchCode] = rule.pricingRuleId;
        }
      });
      setLocalRuleAssignments(assignments);
    }
  }, [formData.pricingConfig?.branchSpecificRules]);

  // Fetch all available pricing rules for dropdowns
  useEffect(() => {
    if (permissions.canManagePricing) {
      dispatch(
        getPricingRulesThunk({
          isActive: true,
          page: 1,
          limit: 100,
        })
      );
    }
  }, [dispatch, permissions.canManagePricing]);

  // Filter rules that are applicable to the current product
  const getApplicableRulesForBranch = (branchCode) => {
    return pricingRules.filter((rule) => {
      if (!rule.isActive) return false;

      const conditions = rule.conditions || {};

      if (conditions.branchCodes?.length > 0) {
        if (!conditions.branchCodes.includes(branchCode)) {
          return false;
        }
      }

      if (
        conditions.productTypes?.length > 0 &&
        !conditions.productTypes.includes(formData.type)
      ) {
        return false;
      }

      if (
        conditions.subTypes?.length > 0 &&
        !conditions.subTypes.includes(formData.subType)
      ) {
        return false;
      }

      const productPurity = formData.metalDetails?.purity || formData.purity;
      if (
        conditions.purityLevels?.length > 0 &&
        productPurity &&
        !conditions.purityLevels.includes(productPurity)
      ) {
        return false;
      }

      const weight = parseFloat(formData.grossWeight) || 0;
      if (conditions.minWeight && weight < conditions.minWeight) {
        return false;
      }
      if (conditions.maxWeight && weight > conditions.maxWeight) {
        return false;
      }

      return true;
    });
  };

  // Handle rule assignment
  const handleRuleAssignment = async (branchCode, ruleId) => {
    const newAssignments = { ...localRuleAssignments };
    if (ruleId) {
      newAssignments[branchCode] = ruleId;
    } else {
      delete newAssignments[branchCode];
    }
    setLocalRuleAssignments(newAssignments);

    const branchSpecificRules = Object.entries(newAssignments).map(
      ([code, id]) => ({
        branchCode: code,
        pricingRuleId: id,
        appliedFrom: new Date(),
        appliedBy: {
          adminId: formData.lastModifiedBy?.adminId || "",
          adminName: formData.lastModifiedBy?.adminName || "",
          role: formData.lastModifiedBy?.role || "",
        },
      })
    );

    onChange({
      target: {
        name: "pricingConfig",
        value: {
          ...formData.pricingConfig,
          branchSpecificRules: branchSpecificRules,
        },
      },
    });

    if (mode === "edit" && productId) {
      try {
        if (ruleId) {
          await dispatch(
            assignPricingRuleThunk({
              productId,
              ruleId,
              branchCode,
            })
          ).unwrap();
          toast.success(`Pricing rule assigned for ${branchCode}`);
        } else {
          await dispatch(
            removePricingRuleThunk({
              productId,
              branchCode,
            })
          ).unwrap();
          toast.success(`Pricing rule removed for ${branchCode}`);
        }
      } catch (error) {
        toast.error(error.message || "Failed to update pricing rule");
        setLocalRuleAssignments(localRuleAssignments);
      }
    }
  };

  const formatRulesForDropdown = (rules) => {
    return rules.map((rule) => ({
      _id: rule._id,
      name: `${rule.name} (Priority: ${rule.priority})`,
      description: rule.description,
    }));
  };

  const branchesToShow =
    locationStocks.length > 0
      ? locationStocks.map((loc) => loc.branchCode)
      : formData.branchInfo?.availableBranches ||
        [formData.branchInfo?.primaryBranch].filter(Boolean);

  const toggleBranchExpand = (branchCode) => {
    setExpandedBranches((prev) => ({
      ...prev,
      [branchCode]: !prev[branchCode],
    }));
  };

  if (!permissions.canManagePricing) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      {/* Header - Responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
        <h3 className="text-base sm:text-lg font-medium">
          Pricing Configuration
        </h3>
        {loadingRules && (
          <span className="text-xs sm:text-sm text-gray-500">
            Loading rules...
          </span>
        )}
      </div>

      <div className="space-y-4">
        {/* Custom Pricing Toggle */}
        <div className="border-b pb-4">
          <label className="flex items-start sm:items-center">
            <input
              type="checkbox"
              name="pricingConfig.useCustomPricing"
              checked={formData.pricingConfig?.useCustomPricing || false}
              onChange={onChange}
              className="rounded border-gray-300 text-primary focus:ring-primary mt-0.5 sm:mt-0"
            />
            <span className="ml-2 text-sm sm:text-base">
              Use custom pricing overrides
            </span>
          </label>

          {formData.pricingConfig?.useCustomPricing && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-4 pl-0 sm:pl-6">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Custom Making Charges
                </label>
                <input
                  type="number"
                  name="pricingConfig.customMakingCharges"
                  value={formData.pricingConfig?.customMakingCharges || ""}
                  onChange={onChange}
                  min="0"
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter amount"
                />
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Custom Wastage %
                </label>
                <input
                  type="number"
                  name="pricingConfig.customWastagePercent"
                  value={formData.pricingConfig?.customWastagePercent || ""}
                  onChange={onChange}
                  min="0"
                  max="100"
                  step="0.1"
                  className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter percentage"
                />
              </div>

              <div className="flex items-end sm:col-span-2 lg:col-span-1">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="pricingConfig.excludeFromOffers"
                    checked={formData.pricingConfig?.excludeFromOffers || false}
                    onChange={onChange}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <span className="ml-2 text-xs sm:text-sm">
                    Exclude from offers
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Branch-Specific Pricing Rules */}
        <div className="space-y-3 sm:space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-2">
            <h4 className="text-sm font-medium text-gray-700">
              Branch-Specific Pricing Rules
            </h4>
            {mode === "add" && (
              <span className="text-xs text-gray-500">
                (Will be applied after saving)
              </span>
            )}
          </div>

          {branchesToShow.length === 0 ? (
            <div className="text-gray-500 text-xs sm:text-sm p-4 bg-gray-50 rounded text-center">
              Add location stock to assign branch-specific pricing rules
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3">
              {branchesToShow.map((branchCode) => {
                const applicableRules = getApplicableRulesForBranch(branchCode);
                const currentRuleId = localRuleAssignments[branchCode];
                const currentRule = pricingRules.find(
                  (r) => r._id === currentRuleId
                );
                const isExpanded = expandedBranches[branchCode];

                return (
                  <div
                    key={branchCode}
                    className="border rounded-lg overflow-hidden"
                  >
                    {/* Mobile: Collapsible Header */}
                    <div
                      className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer sm:cursor-default"
                      onClick={() => toggleBranchExpand(branchCode)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-700">
                          {branchCode}
                        </span>
                        {currentRule && (
                          <span className="hidden sm:inline text-xs text-gray-500 truncate max-w-[200px]">
                            - {currentRule.name}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {currentRuleId && (
                          <span className="sm:hidden text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                            Rule Applied
                          </span>
                        )}
                        <button
                          type="button"
                          className="sm:hidden text-gray-500"
                        >
                          {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                        </button>
                      </div>
                    </div>

                    {/* Desktop: Always visible / Mobile: Collapsible */}
                    <div
                      className={`p-3 border-t bg-white ${
                        isExpanded ? "block" : "hidden"
                      } sm:block`}
                    >
                      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex-1">
                          <Dropdown
                            options={[
                              {
                                _id: "",
                                name: "No specific rule (use default)",
                              },
                              ...formatRulesForDropdown(applicableRules),
                            ]}
                            selected={
                              currentRule
                                ? {
                                    _id: currentRule._id,
                                    name: `${currentRule.name} (Priority: ${currentRule.priority})`,
                                  }
                                : null
                            }
                            onChange={(option) =>
                              handleRuleAssignment(
                                branchCode,
                                option?._id || null
                              )
                            }
                            placeholder="Select pricing rule"
                            searchable={true}
                            disabled={false}
                          />
                        </div>

                        {currentRuleId && (
                          <button
                            type="button"
                            onClick={() =>
                              handleRuleAssignment(branchCode, null)
                            }
                            className="flex items-center justify-center gap-2 text-red-500 hover:text-red-600 px-3 py-2 sm:px-2 sm:py-1 border border-red-200 sm:border-0 rounded-lg sm:rounded-none"
                            title="Remove rule"
                          >
                            <FiTrash2 size={16} />
                            <span className="sm:hidden text-sm">
                              Remove Rule
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Stock Management Section Component
export const StockManagementSection = ({
  formData,
  locationStocks,
  availableBranches,
  permissions,
  onAddLocation,
  onUpdateLocation,
  onRemoveLocation,
  calculateTotalStock,
  onChange,
  errors,
}) => {
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [selectedStore, setSelectedStore] = useState("");
  const [expandedLocations, setExpandedLocations] = useState({});

  const totals = calculateTotalStock();

  const addedBranchCodes = useMemo(() => {
    return locationStocks.map((location) => location.branchCode);
  }, [locationStocks]);

  const branchOptions = useMemo(() => {
    if (!availableBranches || !Array.isArray(availableBranches)) return [];

    return availableBranches
      .map((branch) => {
        const branchCode =
          typeof branch === "object" ? branch.code || branch._id : branch;
        if (addedBranchCodes.includes(branchCode)) return null;

        if (typeof branch === "string") {
          return {
            _id: branch,
            name: branch,
          };
        }

        if (typeof branch === "object" && branch !== null) {
          return {
            _id: branch.code || branch._id,
            name: `${branch.name} (${branch.code || "No code"})`,
          };
        }

        return null;
      })
      .filter(Boolean);
  }, [availableBranches, addedBranchCodes]);

  const handleBranchChange = (option) => {
    setSelectedBranch(option ? option._id : "");
  };

  const handleAddLocation = () => {
    if (!selectedBranch) {
      alert("Please select a branch");
      return;
    }

    onAddLocation(selectedBranch, selectedStore || null);
    setSelectedBranch("");
    setSelectedStore("");
    setShowAddLocation(false);
  };

  const getStockStatus = (available, minLevel) => {
    if (available === 0) return "out-of-stock";
    if (available <= minLevel) return "low-stock";
    return "in-stock";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "out-of-stock":
        return "text-red-600 bg-red-50";
      case "low-stock":
        return "text-yellow-600 bg-yellow-50";
      case "in-stock":
        return "text-green-600 bg-green-50";
      default:
        return "text-gray-600 bg-gray-50";
    }
  };

  const handleNumberInputChange = (index, field, value) => {
    if (value === "") {
      onUpdateLocation(index, field, "");
    } else {
      const numValue = parseInt(value, 10);
      if (!isNaN(numValue)) {
        onUpdateLocation(index, field, numValue);
      }
    }
  };

  const handleNumberInputBlur = (index, field, value) => {
    if (value === "") {
      onUpdateLocation(index, field, 0);
    }
  };

  const toggleLocationExpand = (index) => {
    setExpandedLocations((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  if (!permissions.canManageStock) {
    return (
      <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
        <p className="text-gray-500 text-center text-sm">
          You don't have permission to manage stock
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-4 sm:p-6">
      {/* Header - Responsive */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
        <div>
          <h3 className="text-base sm:text-lg font-medium">
            Stock & Inventory Management
          </h3>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            Manage stock across different branches and stores
          </p>
        </div>

        {/* Stock Summary - Responsive */}
        <div className="flex items-center justify-between sm:justify-start gap-4 sm:gap-6 px-4 py-3 bg-gray-50 rounded-lg">
          <div className="text-center">
            <p className="text-[10px] sm:text-xs text-gray-500">Total</p>
            <p className="text-base sm:text-lg font-semibold">
              {totals.quantity}
            </p>
          </div>
          <div className="text-center">
            <p className="text-[10px] sm:text-xs text-gray-500">Reserved</p>
            <p className="text-base sm:text-lg font-semibold text-orange-600">
              {totals.reserved}
            </p>
          </div>
          <div className="text-center">
            <p className="text-[10px] sm:text-xs text-gray-500">Available</p>
            <p className="text-base sm:text-lg font-semibold text-green-600">
              {totals.available}
            </p>
          </div>
        </div>
      </div>

      {/* Stock Levels Configuration - Responsive */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6">
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
            Minimum Stock Level
          </label>
          <input
            type="number"
            name="stock.minStockLevel"
            value={formData.stock?.minStockLevel || ""}
            onChange={onChange}
            min="0"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Minimum stock alert level"
          />
          <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
            Alert when stock falls below this level
          </p>
        </div>

        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
            Maximum Stock Level
          </label>
          <input
            type="number"
            name="stock.maxStockLevel"
            value={formData.stock?.maxStockLevel || ""}
            onChange={onChange}
            min="0"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
            placeholder="Maximum stock capacity (optional)"
          />
          <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
            Maximum capacity for reorder planning
          </p>
        </div>
      </div>

      {/* Location Stock List */}
      <div className="space-y-3 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
          <h4 className="text-xs sm:text-sm font-medium text-gray-700">
            Location-wise Stock
          </h4>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowAddLocation(true)}
            className="w-full sm:w-auto"
          >
            <FiPlus className="mr-2" />
            Add Location
          </Button>
        </div>

        {locationStocks.length === 0 ? (
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 sm:p-8 text-center">
            <FiPackage className="mx-auto text-3xl sm:text-4xl text-gray-400 mb-2" />
            <p className="text-gray-500 text-sm">No location stock added yet</p>
            <p className="text-xs sm:text-sm text-gray-400 mt-1">
              Add at least one location to manage stock
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {locationStocks.map((location, index) => {
              const status = getStockStatus(
                location.available,
                formData.stock?.minStockLevel || 1
              );
              const isExpanded = expandedLocations[index];

              return (
                <div
                  key={index}
                  className="border rounded-lg overflow-hidden hover:bg-gray-50/50 transition-colors"
                >
                  {/* Location Header - Always visible */}
                  <div
                    className="flex items-center justify-between p-3 sm:p-4 cursor-pointer sm:cursor-default"
                    onClick={() => toggleLocationExpand(index)}
                  >
                    <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                      <FiMapPin className="text-gray-500 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 text-sm sm:text-base truncate">
                          {location.branchCode}
                          {location.storeCode && (
                            <span className="text-gray-500 ml-2 text-xs sm:text-sm">
                              Store: {location.storeCode}
                            </span>
                          )}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <span
                            className={`inline-flex px-2 py-0.5 text-[10px] sm:text-xs rounded-full ${getStatusColor(
                              status
                            )}`}
                          >
                            {status.replace("-", " ").toUpperCase()}
                          </span>
                          {/* Mobile: Show quick stats */}
                          <span className="sm:hidden text-xs text-gray-500">
                            Qty: {location.quantity} | Avl: {location.available}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Desktop: Delete button always visible */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveLocation(index);
                        }}
                        className="hidden sm:flex text-red-600 hover:text-red-700 p-1"
                      >
                        <FiTrash2 />
                      </button>
                      {/* Mobile: Expand indicator */}
                      <button
                        type="button"
                        className="sm:hidden text-gray-500 p-1"
                      >
                        {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
                      </button>
                    </div>
                  </div>

                  {/* Stock Details - Desktop: Always visible / Mobile: Collapsible */}
                  <div
                    className={`border-t bg-gray-50/50 p-3 sm:p-4 ${
                      isExpanded ? "block" : "hidden"
                    } sm:block`}
                  >
                    {/* Desktop: 4-column grid */}
                    <div className="hidden sm:grid sm:grid-cols-4 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Quantity
                        </label>
                        <input
                          type="number"
                          value={
                            location.quantity === "" ? "" : location.quantity
                          }
                          onChange={(e) =>
                            handleNumberInputChange(
                              index,
                              "quantity",
                              e.target.value
                            )
                          }
                          onBlur={(e) =>
                            handleNumberInputBlur(
                              index,
                              "quantity",
                              e.target.value
                            )
                          }
                          min="0"
                          className="w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Reserved
                        </label>
                        <input
                          type="number"
                          value={
                            location.reserved === "" ? "" : location.reserved
                          }
                          onChange={(e) =>
                            handleNumberInputChange(
                              index,
                              "reserved",
                              e.target.value
                            )
                          }
                          onBlur={(e) =>
                            handleNumberInputBlur(
                              index,
                              "reserved",
                              e.target.value
                            )
                          }
                          min="0"
                          max={location.quantity}
                          className="w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Available
                        </label>
                        <input
                          type="number"
                          value={location.available}
                          readOnly
                          className="w-full px-2 py-1.5 text-sm border rounded bg-gray-100"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Display Qty
                        </label>
                        <input
                          type="number"
                          value={
                            location.displayQuantity === ""
                              ? ""
                              : location.displayQuantity
                          }
                          onChange={(e) =>
                            handleNumberInputChange(
                              index,
                              "displayQuantity",
                              e.target.value
                            )
                          }
                          onBlur={(e) =>
                            handleNumberInputBlur(
                              index,
                              "displayQuantity",
                              e.target.value
                            )
                          }
                          min="0"
                          className="w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>
                    </div>

                    {/* Mobile: 2-column grid with better spacing */}
                    <div className="grid grid-cols-2 gap-3 sm:hidden">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Quantity
                        </label>
                        <input
                          type="number"
                          value={
                            location.quantity === "" ? "" : location.quantity
                          }
                          onChange={(e) =>
                            handleNumberInputChange(
                              index,
                              "quantity",
                              e.target.value
                            )
                          }
                          onBlur={(e) =>
                            handleNumberInputBlur(
                              index,
                              "quantity",
                              e.target.value
                            )
                          }
                          min="0"
                          className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Reserved
                        </label>
                        <input
                          type="number"
                          value={
                            location.reserved === "" ? "" : location.reserved
                          }
                          onChange={(e) =>
                            handleNumberInputChange(
                              index,
                              "reserved",
                              e.target.value
                            )
                          }
                          onBlur={(e) =>
                            handleNumberInputBlur(
                              index,
                              "reserved",
                              e.target.value
                            )
                          }
                          min="0"
                          max={location.quantity}
                          className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Available
                        </label>
                        <input
                          type="number"
                          value={location.available}
                          readOnly
                          className="w-full px-3 py-2 text-sm border rounded-lg bg-gray-100"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Display Qty
                        </label>
                        <input
                          type="number"
                          value={
                            location.displayQuantity === ""
                              ? ""
                              : location.displayQuantity
                          }
                          onChange={(e) =>
                            handleNumberInputChange(
                              index,
                              "displayQuantity",
                              e.target.value
                            )
                          }
                          onBlur={(e) =>
                            handleNumberInputBlur(
                              index,
                              "displayQuantity",
                              e.target.value
                            )
                          }
                          min="0"
                          className="w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>
                    </div>

                    {/* Mobile: Delete button */}
                    <button
                      type="button"
                      onClick={() => onRemoveLocation(index)}
                      className="sm:hidden flex items-center justify-center gap-2 w-full mt-3 py-2 text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                    >
                      <FiTrash2 size={16} />
                      <span className="text-sm">Remove Location</span>
                    </button>

                    {location.available < 0 && (
                      <div className="flex items-center gap-2 mt-3 text-red-600">
                        <FiAlertTriangle className="text-sm flex-shrink-0" />
                        <span className="text-xs">
                          Reserved quantity exceeds total quantity
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {errors.stock && (
          <p className="text-xs sm:text-sm text-red-500 mt-2">{errors.stock}</p>
        )}
      </div>

      {/* Add Location Modal - Responsive */}
      {showAddLocation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-lg p-5 sm:p-6 w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
            {/* Mobile drag indicator */}
            <div className="sm:hidden w-12 h-1 bg-gray-300 rounded-full mx-auto mb-4" />

            <h3 className="text-base sm:text-lg font-medium mb-4">
              Add Stock Location
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Branch *
                </label>
                {branchOptions.length > 0 ? (
                  <Dropdown
                    options={branchOptions}
                    selected={
                      selectedBranch
                        ? branchOptions.find(
                            (opt) => opt._id === selectedBranch
                          ) || null
                        : null
                    }
                    onChange={handleBranchChange}
                    placeholder="Select a branch"
                    searchable={true}
                    isOptionsLoading={!Array.isArray(availableBranches)}
                    noOptionsMessage={
                      addedBranchCodes.length === availableBranches?.length
                        ? "All branches have already been added"
                        : "No branches available"
                    }
                  />
                ) : (
                  <div className="p-3 bg-gray-50 rounded-lg border text-gray-500 text-xs sm:text-sm">
                    {addedBranchCodes.length === availableBranches?.length
                      ? "All branches have already been added"
                      : "Loading branches..."}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Store Code (Optional)
                </label>
                <input
                  type="text"
                  value={selectedStore}
                  onChange={(e) => setSelectedStore(e.target.value)}
                  className="w-full px-3 py-2.5 sm:py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  placeholder="Enter store code if applicable"
                />
              </div>
            </div>

            {/* Action buttons - Stack on mobile */}
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 sm:gap-2 mt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowAddLocation(false);
                  setSelectedBranch("");
                  setSelectedStore("");
                }}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleAddLocation}
                disabled={!selectedBranch}
                className="w-full sm:w-auto"
              >
                Add Location
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const BranchInfoDisplay = ({ branchInfo, locationStocks }) => {
  const branches = [...new Set(locationStocks.map((loc) => loc.branchCode))];
  const stores = [
    ...new Set(
      locationStocks.filter((loc) => loc.storeCode).map((loc) => loc.storeCode)
    ),
  ];

  return (
    <div className="bg-blue-50 rounded-lg p-3 sm:p-4 mt-4">
      <h4 className="text-xs sm:text-sm font-medium text-blue-900 mb-2">
        Branch Distribution
      </h4>
      <div className="space-y-1.5 sm:space-y-2">
        <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
          <span className="text-[10px] sm:text-xs text-blue-700 font-medium">
            Primary:
          </span>
          <span className="text-[10px] sm:text-xs text-blue-900">
            {branchInfo.primaryBranch}
          </span>
        </div>
        {branches.length > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
            <span className="text-[10px] sm:text-xs text-blue-700 font-medium whitespace-nowrap">
              Available at:
            </span>
            <span className="text-[10px] sm:text-xs text-blue-900 break-words">
              {branches.join(", ")}
            </span>
          </div>
        )}
        {stores.length > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-2">
            <span className="text-[10px] sm:text-xs text-blue-700 font-medium">
              Stores:
            </span>
            <span className="text-[10px] sm:text-xs text-blue-900 break-words">
              {stores.join(", ")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
